// Detection + review-queue helpers for multi-leg trips -- see
// supabase/migrations/0046_multi_leg_groups.sql for the "why". Candidates
// are computed live (not pre-stored); only a human-confirmed group in
// multi_leg_groups actually changes capacity behaviour, consumed by both
// placeUnassignedJobs (src/components/fleet-capacity/slotSheetUtils.ts) and
// calculate-capacity-slot's unassigned-prebooking loop -- keep those three
// in lockstep with this file's shape if it changes.
import { supabase } from "@/lib/supabase";
import { nzDateStrFromIso } from "@/components/fleet-capacity/utils";

export interface MultiLegSettings {
  detectionWindowMinutes: number;
  bufferMinutes: number;
}

export const DEFAULT_MULTI_LEG_SETTINGS: MultiLegSettings = {
  detectionWindowMinutes: 20,
  bufferMinutes: 5,
};

const SETTING_NAMES = {
  detectionWindowMinutes: "multi_leg_detection_window_minutes",
  bufferMinutes: "multi_leg_buffer_minutes",
} as const;

export async function loadMultiLegSettings(): Promise<MultiLegSettings> {
  const { data, error } = await supabase.from("settings").select("name, value").in("name", Object.values(SETTING_NAMES));
  if (error || !data) return DEFAULT_MULTI_LEG_SETTINGS;

  const byName = new Map(data.map((row) => [row.name, row.value]));
  const settings = { ...DEFAULT_MULTI_LEG_SETTINGS };
  const dw = parseFloat(String(byName.get(SETTING_NAMES.detectionWindowMinutes) ?? ""));
  if (!Number.isNaN(dw) && dw > 0) settings.detectionWindowMinutes = dw;
  const bm = parseFloat(String(byName.get(SETTING_NAMES.bufferMinutes) ?? ""));
  if (!Number.isNaN(bm) && bm >= 0) settings.bufferMinutes = bm;
  return settings;
}

const CLOSED_STAGES = new Set(["Delivered", "Cancelled", "No show"]);

export interface MultiLegCandidateLeg {
  job_id: string;
  passenger_first_name: string | null;
  passenger_last_name: string | null;
  pick_up_location: string | null;
  drop_off_location: string | null;
  requested_pickup_at: string | null;
  estimated_duration_seconds: number | null;
}

export interface MultiLegCandidate {
  account_id: string;
  account_name: string | null;
  legs: MultiLegCandidateLeg[];
}

interface RawTaxicallerRow {
  Job_ID: string | null;
  Account_ID: string | null;
  Passenger_First_Name: string | null;
  Passenger_Last_Name: string | null;
  Pick_Up_Location: string | null;
  Drop_Off_Location: string | null;
  requested_pickup_at: string | null;
  estimated_duration_seconds: number | null;
  Stage: string | null;
}

/**
 * Unassigned, open-stage bookings for the given NZ calendar date, grouped by
 * Account_ID and then clustered by time proximity (a gap of more than
 * windowMinutes between consecutive requested times starts a new cluster).
 * Only clusters of 2+ legs are candidates. Any Job_ID already covered by an
 * existing multi_leg_groups row (any status) is excluded so a reviewed
 * cluster doesn't reappear.
 */
export async function fetchMultiLegCandidates(dateStr: string, windowMinutes: number): Promise<MultiLegCandidate[]> {
  // Unassigned rows across the whole table commonly number in the
  // thousands, well past PostgREST's default 1000-row cap -- an unordered,
  // unbounded query here can silently drop today's rows while keeping
  // stale ones. Bound to the target NZ calendar date at the DB level
  // (padded a day each side for the UTC/NZ offset, same pattern as
  // calculate-capacity-slot's preDispatchJobs query), then the existing
  // nzDateStrFromIso filter below narrows to the exact date.
  const dayBeforeUtc = new Date(`${dateStr}T00:00:00Z`);
  dayBeforeUtc.setUTCDate(dayBeforeUtc.getUTCDate() - 1);
  const dayAfterUtc = new Date(`${dateStr}T00:00:00Z`);
  dayAfterUtc.setUTCDate(dayAfterUtc.getUTCDate() + 2);

  const { data, error } = await supabase
    .from("taxicaller_data")
    .select(
      '"Job_ID", "Account_ID", "Passenger_First_Name", "Passenger_Last_Name", "Pick_Up_Location", "Drop_Off_Location", requested_pickup_at, estimated_duration_seconds, "Stage"'
    )
    .is("vehicle_number", null)
    .not("Account_ID", "is", null)
    .not("requested_pickup_at", "is", null)
    .gte("requested_pickup_at", dayBeforeUtc.toISOString())
    .lte("requested_pickup_at", dayAfterUtc.toISOString());
  if (error || !data) return [];

  const { data: existingGroups } = await supabase.from("multi_leg_groups").select("job_ids");
  const alreadyReviewed = new Set((existingGroups || []).flatMap((g) => (g.job_ids as string[]) || []));

  const rows = (data as RawTaxicallerRow[]).filter(
    (r) =>
      !CLOSED_STAGES.has(r.Stage || "") &&
      !!r.Job_ID &&
      !alreadyReviewed.has(r.Job_ID) &&
      nzDateStrFromIso(r.requested_pickup_at) === dateStr
  );

  const byAccount = new Map<string, RawTaxicallerRow[]>();
  rows.forEach((r) => {
    const key = r.Account_ID as string;
    if (!byAccount.has(key)) byAccount.set(key, []);
    byAccount.get(key)!.push(r);
  });

  const candidates: MultiLegCandidate[] = [];
  for (const [accountId, accountRows] of byAccount) {
    const sorted = [...accountRows].sort(
      (a, b) => new Date(a.requested_pickup_at as string).getTime() - new Date(b.requested_pickup_at as string).getTime()
    );
    let cluster: RawTaxicallerRow[] = [];
    const flush = () => {
      if (cluster.length >= 2) {
        candidates.push({
          account_id: accountId,
          account_name: null,
          legs: cluster.map((r) => ({
            job_id: r.Job_ID as string,
            passenger_first_name: r.Passenger_First_Name,
            passenger_last_name: r.Passenger_Last_Name,
            pick_up_location: r.Pick_Up_Location,
            drop_off_location: r.Drop_Off_Location,
            requested_pickup_at: r.requested_pickup_at,
            estimated_duration_seconds: r.estimated_duration_seconds,
          })),
        });
      }
      cluster = [];
    };
    for (const row of sorted) {
      if (cluster.length === 0) {
        cluster.push(row);
        continue;
      }
      const prev = cluster[cluster.length - 1];
      const gapMin = (new Date(row.requested_pickup_at as string).getTime() - new Date(prev.requested_pickup_at as string).getTime()) / 60000;
      if (gapMin <= windowMinutes) cluster.push(row);
      else {
        flush();
        cluster.push(row);
      }
    }
    flush();
  }

  const accountIds = Array.from(new Set(candidates.map((c) => c.account_id)));
  if (accountIds.length > 0) {
    const { data: accounts } = await supabase.from("crm_accounts").select("account_id, account_name").in("account_id", accountIds);
    const nameById = new Map((accounts || []).map((a) => [a.account_id, a.account_name]));
    candidates.forEach((c) => {
      c.account_name = nameById.get(c.account_id) || null;
    });
  }

  return candidates;
}

export async function confirmMultiLegGroup(accountId: string, jobIds: string[]): Promise<void> {
  const { error } = await supabase.from("multi_leg_groups").insert({
    account_id: accountId,
    job_ids: jobIds,
    status: "confirmed",
    reviewed_at: new Date().toISOString(),
  });
  if (error) throw error;
}

export async function rejectMultiLegGroup(accountId: string, jobIds: string[]): Promise<void> {
  const { error } = await supabase.from("multi_leg_groups").insert({
    account_id: accountId,
    job_ids: jobIds,
    status: "rejected",
    reviewed_at: new Date().toISOString(),
  });
  if (error) throw error;
}

export interface ConfirmedMultiLegGroup {
  id: string;
  job_ids: string[];
  buffer_minutes: number | null;
  /** The real vehicle_number TaxiCaller/a dispatcher has already assigned to
   *  one or more of this group's legs, if any. Fleet Capacity never actually
   *  dispatches anything -- it only estimates capacity -- so once part of a
   *  confirmed group has a real assignment, the remaining still-unassigned
   *  legs should be shown continuing on that SAME vehicle instead of being
   *  placed onto a different one via a fresh gap search. */
  assigned_vehicle_number: string | null;
}

/** Consumed by placeUnassignedJobs and calculate-capacity-slot to collapse
 * a confirmed group's legs into one vehicle's combined workload. */
export async function fetchConfirmedMultiLegGroups(): Promise<ConfirmedMultiLegGroup[]> {
  const { data, error } = await supabase.from("multi_leg_groups").select("id, job_ids, buffer_minutes").eq("status", "confirmed");
  if (error || !data) return [];

  const allJobIds = Array.from(new Set(data.flatMap((g) => (g.job_ids as string[]) || [])));
  const vehicleByJobId = new Map<string, string>();
  if (allJobIds.length > 0) {
    const { data: assignedRows } = await supabase
      .from("taxicaller_data")
      .select('"Job_ID", vehicle_number')
      .in("Job_ID", allJobIds)
      .not("vehicle_number", "is", null);
    ((assignedRows || []) as { Job_ID: string; vehicle_number: string }[]).forEach((r) => vehicleByJobId.set(r.Job_ID, r.vehicle_number));
  }

  return data.map((g) => ({
    ...g,
    assigned_vehicle_number: (g.job_ids as string[]).map((jid) => vehicleByJobId.get(jid)).find((v): v is string => v != null) ?? null,
  }));
}
